// PDP buy zone (sections/vita-main-product.liquid, P4-02). Everything is server rendered and works
// without JS: the gallery track scrolls natively, the thumbnails are links to the slides, every image
// shows, and the chips are anchor links. This adds:
// - <vita-pdp-gallery>: the "1 / 11" counter and current thumbnail follow the swipe; a thumbnail scrolls
//   the track only (not the page); "View all N images" (from 1100) opens the rest of the stack; the
//   track follows vita:variant-change to the variant's image. Fires vita:gallery_interact
//   {product_id, action: swipe | thumb | arrow | view_all, index, count}. With data-gallery="strip" (product.short)
//   the track is a swipe carousel at every width (one image at a time): the thumbnails and the strip
//   arrows (data-vita-gallery-prev / next) move to the image.
// - <vita-sticky-panel>: from 1100 the buy panel is sticky; when it is taller than the viewport its top
//   goes negative so its end (the accordion) is still reached while the gallery scrolls.
// - the chips hide when their module is not on the page, and "Directions" opens that accordion row.

const REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)');
const DESKTOP = window.matchMedia('(min-width: 1100px)');

class VitaPdpGallery extends HTMLElement {
  #observer;
  #index = 1;
  #target = null;
  #settle;
  #release;
  #syncFocus;

  connectedCallback() {
    this.track = this.querySelector('[data-vita-gallery-track]');
    if (!this.track) return;
    this.slides = [...this.track.querySelectorAll('[data-vita-gallery-slide]')];
    this.thumbs = [...this.querySelectorAll('[data-vita-gallery-thumb]')];
    this.current = this.querySelector('[data-vita-gallery-current]');
    this.all = this.querySelector('[data-vita-gallery-all]');
    this.prev = this.querySelector('[data-vita-gallery-prev]');
    this.next = this.querySelector('[data-vita-gallery-next]');

    // the track takes focus (arrow keys scroll it) only while it is a carousel
    this.#syncFocus = () => (this.track.tabIndex = this.#carousel ? 0 : -1);
    DESKTOP.addEventListener('change', this.#syncFocus);
    this.#syncFocus();

    // strip style: the arrows step the main image (they stay hidden without JS, the thumbnails are links)
    for (const [button, step] of [[this.prev, -1], [this.next, 1]]) {
      if (!button) continue;
      button.hidden = false;
      button.addEventListener('click', () => this.#step(step));
    }
    this.#syncArrows(1);

    this.#observe();
    DESKTOP.addEventListener('change', this.#observe);
    this.addEventListener('click', this.#onClick);
    this.all?.addEventListener('click', this.#onAll);
    document.addEventListener('vita:variant-change', this.#onVariant);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    DESKTOP.removeEventListener('change', this.#syncFocus);
    DESKTOP.removeEventListener('change', this.#observe);
    document.removeEventListener('vita:variant-change', this.#onVariant);
  }

  get #count() {
    return this.slides.length;
  }

  // a swipe carousel below 1100; from 1100 every style is a stack the page scrolls through
  get #carousel() {
    // the strip style (product.short) is one image with the thumbnail strip and arrows at every width, as in the design
    return !DESKTOP.matches || this.dataset.gallery === 'strip';
  }

  #step(delta) {
    const index = Math.min(Math.max(this.#index + delta, 1), this.#count);
    if (index === this.#index) return;
    this.#goTo(index);
    this.#track('arrow', index);
  }

  #syncArrows(index) {
    if (this.prev) this.prev.disabled = index <= 1;
    if (this.next) this.next.disabled = index >= this.#count;
  }

  // images shown in the desktop stack before "View all"
  get #limit() {
    return Number(this.dataset.limit) || 7;
  }

  #track(action, index) {
    window.vitaTrack?.('gallery_interact', {
      product_id: Number(this.dataset.productId),
      action,
      index,
      count: this.#count,
    });
  }

  #setCurrent(index) {
    if (index === this.#index) return;
    this.#index = index;
    this.#syncUi(index);
    // scrolling the page past the stack tiles is not a gallery interaction: the thumbnails just follow it
    if (!this.#carousel) return;
    // a thumbnail or variant scroll passes slides on the way: only its target counts, and no event
    if (this.#target != null) {
      if (index === this.#target) this.#target = null;
      return;
    }
    // one event per settled swipe, not per frame
    clearTimeout(this.#settle);
    this.#settle = setTimeout(() => this.#track('swipe', index), 400);
  }

  #syncUi(index) {
    if (this.current) this.current.textContent = String(index);
    this.#syncArrows(index);
    for (const t of this.thumbs) {
      const on = Number(t.dataset.vitaGalleryThumb) === index;
      if (on) {
        t.setAttribute('aria-current', 'true');
        // keep the current thumbnail in its row without scrolling the page
        const row = t.closest('ul');
        const item = t.parentElement;
        if (row && item) {
          if (item.offsetLeft < row.scrollLeft) row.scrollLeft = item.offsetLeft;
          else if (item.offsetLeft + item.offsetWidth > row.scrollLeft + row.clientWidth) row.scrollLeft = item.offsetLeft + item.offsetWidth - row.clientWidth;
        }
      } else t.removeAttribute('aria-current');
    }
  }

  // carousel: the slide that is 60% inside the track. Strip stack: the tile that crosses the middle of the screen
  #observe = () => {
    this.#observer?.disconnect();
    if (this.#carousel) this.#observer = new IntersectionObserver(this.#onIntersect, { root: this.track, threshold: 0.6 });
    else if (this.dataset.gallery === 'strip') this.#observer = new IntersectionObserver(this.#onIntersect, { rootMargin: '-50% 0px -50% 0px' });
    else {
      this.#observer = null;
      return;
    }
    this.slides.forEach((s) => this.#observer.observe(s));
  };

  #onIntersect = (entries) => {
    for (const e of entries) {
      if (e.isIntersecting) this.#setCurrent(Number(e.target.dataset.index));
    }
  };

  #goTo(index) {
    const slide = this.slides[index - 1];
    if (!slide) return;
    if (!this.#carousel) {
      if (index > this.#limit) this.#expand(false);
      // the strip stack puts the tile at the top of the screen (scroll-margin clears the header)
      slide.scrollIntoView({ behavior: REDUCE.matches ? 'auto' : 'smooth', block: this.dataset.gallery === 'strip' ? 'start' : 'nearest' });
      return;
    }
    if (index === this.#index) return;
    clearTimeout(this.#settle);
    this.#target = index;
    // a swipe that interrupts the scroll takes over again
    clearTimeout(this.#release);
    this.#release = setTimeout(() => (this.#target = null), 1200);
    this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: REDUCE.matches ? 'auto' : 'smooth' });
  }

  #onClick = (e) => {
    const thumb = e.target.closest('[data-vita-gallery-thumb]');
    if (!thumb || e.metaKey || e.ctrlKey || e.shiftKey) return;
    e.preventDefault();
    const index = Number(thumb.dataset.vitaGalleryThumb);
    this.#goTo(index);
    this.#track('thumb', index);
  };

  #expand(track = true) {
    if (this.hasAttribute('data-expanded')) return;
    this.setAttribute('data-expanded', '');
    this.all?.setAttribute('aria-expanded', 'true');
    if (track) this.#track('view_all', this.#index);
  }

  #onAll = () => {
    this.#expand();
    // move focus to the first image that just appeared
    const first = this.slides[this.#limit];
    if (first) {
      first.tabIndex = -1;
      first.focus({ preventScroll: false });
    }
  };


  #onVariant = (e) => {
    if (String(e.detail?.productId) !== this.dataset.productId) return;
    const mediaId = e.detail?.mediaId;
    if (!mediaId) return;
    const slide = this.slides.find((s) => s.dataset.mediaId === String(mediaId));
    if (slide) this.#goTo(Number(slide.dataset.index));
  };
}

// the CSS works out the top from --vita-panel-h and Horizon's --header-height (kept by vita-header.js)
class VitaStickyPanel extends HTMLElement {
  #observer;

  connectedCallback() {
    this.#observer = new ResizeObserver(() => this.style.setProperty('--vita-panel-h', `${this.offsetHeight}px`));
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }
}

function initChips() {
  for (const list of document.querySelectorAll('[data-vita-pdp-chips]')) {
    for (const chip of list.querySelectorAll('[data-vita-chip-target]')) {
      if (!document.getElementById(chip.dataset.vitaChipTarget)) chip.closest('li')?.setAttribute('hidden', '');
    }
    list.addEventListener('click', (e) => {
      const chip = e.target.closest('[data-vita-chip-target]');
      if (!chip) return;
      const target = document.getElementById(chip.dataset.vitaChipTarget);
      if (!target) return;
      e.preventDefault();
      const row = target.querySelector('details[data-question-id="product_directions"]');
      if (row) row.open = true;
      (row || target).scrollIntoView({ behavior: REDUCE.matches ? 'auto' : 'smooth', block: 'start' });
      history.replaceState(history.state, '', `#${target.id}`);
    });
  }
}

if (!customElements.get('vita-pdp-gallery')) customElements.define('vita-pdp-gallery', VitaPdpGallery);
if (!customElements.get('vita-sticky-panel')) customElements.define('vita-sticky-panel', VitaStickyPanel);
initChips();
